import {
  Suspense,
  lazy,
  type ComponentType,
  type JSX,
  type LazyExoticComponent,
} from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Permission, UserRole } from '@manpower/shared';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RequireCompany } from '@/auth/RequireCompany';
import { HashScroll } from '@/components/HashScroll';
import { AppShell } from '@/components/layout/AppShell';
import { PublicShell } from '@/components/layout/PublicShell';
import { PageSpinner } from '@/components/primitives';

/**
 * Every page is split out of the entry bundle.
 *
 * Without this the whole app — all three portals — ships as one 737 kB chunk, so
 * a candidate opening a job ad on a 4G phone pays to download the admin back
 * office before anything paints. The shells stay eager: they are small and every
 * signed-in route needs them immediately.
 */
// Generic over the module so the export name is checked and the component's own
// props survive — DeploymentsPage takes a `scope`.
function page<M, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
): M[K] extends ComponentType<infer P> ? LazyExoticComponent<ComponentType<P>> : never {
  return lazy(async () => ({
    default: (await loader())[name] as ComponentType,
  })) as never;
}

// --- Public ---------------------------------------------------------------
const Home = page(() => import('@/pages/public/home/Home'), 'Home');
const JobList = page(() => import('@/pages/public/JobList'), 'JobList');
const JobDetail = page(() => import('@/pages/public/JobDetail'), 'JobDetail');
const Companies = page(() => import('@/pages/public/Companies'), 'Companies');
const CompanyDetailPage = page(() => import('@/pages/public/Companies'), 'CompanyDetailPage');
const ContentPage = page(() => import('@/pages/public/ContentPage'), 'ContentPage');
const NotFound = page(() => import('@/pages/NotFound'), 'NotFound');

// --- Marketing, legal and trust -------------------------------------------
// Coded routes rather than CMS rows: these carry tabs, forms and calls to
// action, and ContentPage renders its body as plain text on purpose.
const About = page(() => import('@/pages/public/site/About'), 'About');
const Services = page(() => import('@/pages/public/site/Services'), 'Services');
const Employers = page(() => import('@/pages/public/site/Employers'), 'Employers');
const JobSeekers = page(() => import('@/pages/public/site/JobSeekers'), 'JobSeekers');
const Contact = page(() => import('@/pages/public/site/Contact'), 'Contact');
const Faq = page(() => import('@/pages/public/site/Faq'), 'Faq');
const Safety = page(() => import('@/pages/public/site/Safety'), 'Safety');
const ReportFraud = page(() => import('@/pages/public/site/ReportFraud'), 'ReportFraud');
const EmployerVerification = page(
  () => import('@/pages/public/site/EmployerVerification'),
  'EmployerVerification',
);
const PrivacyPolicy = page(() => import('@/pages/public/site/PrivacyPolicy'), 'PrivacyPolicy');
const PrivacyChoices = page(() => import('@/pages/public/site/PrivacyChoices'), 'PrivacyChoices');
const Terms = page(() => import('@/pages/public/site/Terms'), 'Terms');
const EmployerTerms = page(() => import('@/pages/public/site/EmployerTerms'), 'EmployerTerms');
const JobSeekerTerms = page(() => import('@/pages/public/site/JobSeekerTerms'), 'JobSeekerTerms');
const Accessibility = page(() => import('@/pages/public/site/Accessibility'), 'Accessibility');

// --- Auth -----------------------------------------------------------------
const Login = page(() => import('@/pages/auth/Login'), 'Login');
const EmployerSignup = page(() => import('@/pages/auth/EmployerSignup'), 'EmployerSignup');

// --- Job seeker -----------------------------------------------------------
const SeekerDashboard = page(() => import('@/pages/seeker/SeekerDashboard'), 'SeekerDashboard');
const SeekerProfile = page(() => import('@/pages/seeker/SeekerProfile'), 'SeekerProfile');
const SeekerApplications = page(
  () => import('@/pages/seeker/SeekerApplications'),
  'SeekerApplications',
);
const SeekerOffers = page(() => import('@/pages/seeker/SeekerOffers'), 'SeekerOffers');
const SeekerSavedJobs = page(() => import('@/pages/seeker/SeekerSavedJobs'), 'SeekerSavedJobs');
const SeekerAlerts = page(() => import('@/pages/seeker/SeekerAlerts'), 'SeekerAlerts');
const SeekerDocuments = page(() => import('@/pages/seeker/SeekerDocuments'), 'SeekerDocuments');
const SeekerPayslips = page(() => import('@/pages/seeker/SeekerPayslips'), 'SeekerPayslips');

// --- Employer -------------------------------------------------------------
const EmployerDashboard = page(
  () => import('@/pages/employer/EmployerDashboard'),
  'EmployerDashboard',
);
const EmployerJobs = page(() => import('@/pages/employer/EmployerJobs'), 'EmployerJobs');
const EmployerJobForm = page(() => import('@/pages/employer/EmployerJobForm'), 'EmployerJobForm');
const PipelineBoard = page(() => import('@/pages/employer/PipelineBoard'), 'PipelineBoard');
const EmployerApplicants = page(
  () => import('@/pages/employer/EmployerApplicants'),
  'EmployerApplicants',
);
const EmployerCandidates = page(
  () => import('@/pages/employer/EmployerCandidates'),
  'EmployerCandidates',
);
const EmployerInterviews = page(
  () => import('@/pages/employer/EmployerInterviews'),
  'EmployerInterviews',
);
const EmployerCompany = page(() => import('@/pages/employer/EmployerCompany'), 'EmployerCompany');
const EmployerReports = page(() => import('@/pages/employer/EmployerReports'), 'EmployerReports');
const EmployerCompanySetup = page(
  () => import('@/pages/employer/EmployerCompanySetup'),
  'EmployerCompanySetup',
);

// --- Back office ----------------------------------------------------------
const AdminDashboard = page(() => import('@/pages/admin/AdminDashboard'), 'AdminDashboard');
const AdminEmployers = page(() => import('@/pages/admin/AdminEmployers'), 'AdminEmployers');
const AdminEmployerDetail = page(
  () => import('@/pages/admin/AdminEmployerDetail'),
  'AdminEmployerDetail',
);
const AdminVerificationQueue = page(
  () => import('@/pages/admin/AdminVerificationQueue'),
  'AdminVerificationQueue',
);
const AdminJobs = page(() => import('@/pages/admin/AdminJobs'), 'AdminJobs');
const AdminSeekers = page(() => import('@/pages/admin/AdminSeekers'), 'AdminSeekers');
const AdminDocuments = page(() => import('@/pages/admin/AdminDocuments'), 'AdminDocuments');
const AdminRequirements = page(() => import('@/pages/admin/AdminRequirements'), 'AdminRequirements');
const AdminReports = page(() => import('@/pages/admin/AdminReports'), 'AdminReports');
const AdminContent = page(() => import('@/pages/admin/AdminContent'), 'AdminContent');
const AdminStaff = page(() => import('@/pages/admin/AdminStaff'), 'AdminStaff');
const AdminAudit = page(() => import('@/pages/admin/AdminAudit'), 'AdminAudit');
const AdminTrust = page(() => import('@/pages/admin/AdminTrust'), 'AdminTrust');

// --- Shared ---------------------------------------------------------------
const DeploymentsPage = page(() => import('@/pages/shared/DeploymentsPage'), 'DeploymentsPage');
const AttendancePage = page(() => import('@/pages/shared/AttendancePage'), 'AttendancePage');
const InvoicesPage = page(() => import('@/pages/shared/InvoicesPage'), 'InvoicesPage');
const InvoiceDetailPage = page(
  () => import('@/pages/shared/InvoiceDetailPage'),
  'InvoiceDetailPage',
);
const PayrollPage = page(() => import('@/pages/shared/PayrollPage'), 'PayrollPage');
const PayrollRunDetailPage = page(
  () => import('@/pages/shared/PayrollRunDetailPage'),
  'PayrollRunDetailPage',
);

const ADMIN_ROLES = [UserRole.STAFF, UserRole.ADMIN, UserRole.SUPER_ADMIN];

/** Wraps a back-office page in its own permission gate. */
const gated = (permission: Permission, element: JSX.Element) => (
  <ProtectedRoute roles={ADMIN_ROLES} permissions={[permission]}>
    {element}
  </ProtectedRoute>
);

export function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <HashScroll />
      <Routes>
        {/* Anonymous — job search has to be crawlable and usable without an
            account, or candidates never sign up in the first place. */}
        <Route element={<PublicShell />}>
          <Route index element={<Home />} />
          <Route path="jobs" element={<JobList />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          <Route path="companies" element={<Companies />} />
          <Route path="companies/:slug" element={<CompanyDetailPage />} />

          {/* Marketing and audience pages */}
          <Route path="about" element={<About />} />
          <Route path="services" element={<Services />} />
          <Route path="employers" element={<Employers />} />
          <Route path="job-seekers" element={<JobSeekers />} />

          {/* Trust. These are conversion pages, not fine print: the fraud banner
              on every page points here, so they must never sit behind a login. */}
          <Route path="safety" element={<Safety />} />
          <Route path="report-fraud" element={<ReportFraud />} />
          <Route path="employer-verification" element={<EmployerVerification />} />
          <Route path="contact" element={<Contact />} />
          <Route path="faq" element={<Faq />} />

          {/* Legal */}
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="privacy-choices" element={<PrivacyChoices />} />
          <Route path="terms" element={<Terms />} />
          <Route path="employer-terms" element={<EmployerTerms />} />
          <Route path="job-seeker-terms" element={<JobSeekerTerms />} />
          <Route path="accessibility" element={<Accessibility />} />

          {/*
            The CMS still serves any other slug, so a marketing page can be added
            without a deploy. The four slugs below were the original home of this
            content and are in the sitemap, in search results and in printed
            material, so they redirect rather than 404 — and redirect rather than
            render, so the placeholder legal copy still sitting in the database
            cannot be reached.
          */}
          <Route path="page/about" element={<Navigate to="/about" replace />} />
          <Route path="page/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="page/terms" element={<Navigate to="/terms" replace />} />
          <Route path="page/faq" element={<Navigate to="/faq" replace />} />
          <Route path="page/:slug" element={<ContentPage />} />

          <Route path="login" element={<Login />} />
          {/*
            Employers used to have their own entrance. Kept as a redirect
            rather than deleted: the address is in bookmarks, in email
            signatures and on printed material, and landing on a 404 would
            read as the site being broken.
          */}
          <Route path="employer/login" element={<Navigate to="/login" replace />} />
          <Route path="employer/signup" element={<EmployerSignup />} />
        </Route>

        {/* Job seeker */}
        <Route element={<ProtectedRoute roles={[UserRole.SEEKER]} />}>
          <Route path="seeker" element={<AppShell />}>
            <Route index element={<SeekerDashboard />} />
            <Route path="profile" element={<SeekerProfile />} />
            <Route path="applications" element={<SeekerApplications />} />
            <Route path="offers" element={<SeekerOffers />} />
            <Route path="saved" element={<SeekerSavedJobs />} />
            <Route path="alerts" element={<SeekerAlerts />} />
            <Route path="documents" element={<SeekerDocuments />} />
            <Route path="payslips" element={<SeekerPayslips />} />
          </Route>
        </Route>

        {/* Employer */}
        <Route element={<ProtectedRoute roles={[UserRole.EMPLOYER]} />}>
          <Route path="employer" element={<AppShell />}>
            {/* The one employer route reachable without a company — everything
                below the guard needs one, and this is where they go to create
                it. Wrapping this whole group in <RequireCompany /> would
                redirect this route to itself, forever. */}
            <Route path="company/setup" element={<EmployerCompanySetup />} />
            <Route element={<RequireCompany />}>
              <Route index element={<EmployerDashboard />} />
              <Route path="jobs" element={<EmployerJobs />} />
              <Route path="jobs/new" element={<EmployerJobForm />} />
              <Route path="jobs/:id" element={<EmployerJobForm />} />
              <Route path="applicants" element={<EmployerApplicants />} />
              <Route path="pipeline" element={<PipelineBoard scope="employer" />} />
              <Route path="candidates" element={<EmployerCandidates />} />
              <Route path="interviews" element={<EmployerInterviews />} />
              <Route path="deployments" element={<DeploymentsPage scope="employer" />} />
              <Route path="attendance" element={<AttendancePage scope="employer" />} />
              <Route path="invoices" element={<InvoicesPage scope="employer" />} />
              <Route path="invoices/:id" element={<InvoiceDetailPage scope="employer" />} />
              <Route path="reports" element={<EmployerReports />} />
              <Route path="company" element={<EmployerCompany />} />
            </Route>
          </Route>
        </Route>

        {/* Back office. Route-level gating mirrors the API's permission checks so
            a STAFF account sees only what it can actually use — the API re-checks
            every request regardless. */}
        <Route element={<ProtectedRoute roles={ADMIN_ROLES} />}>
          <Route path="admin" element={<AppShell />}>
            <Route index element={<AdminDashboard />} />
            <Route
              path="verification"
              element={gated(Permission.EMPLOYER_VIEW, <AdminVerificationQueue />)}
            />
            <Route path="employers" element={gated(Permission.EMPLOYER_VIEW, <AdminEmployers />)} />
            <Route
              path="employers/:id"
              element={gated(Permission.EMPLOYER_VIEW, <AdminEmployerDetail />)}
            />
            <Route path="jobs" element={gated(Permission.JOB_VIEW, <AdminJobs />)} />
            <Route
              path="pipeline"
              element={gated(Permission.APPLICATION_VIEW, <PipelineBoard scope="admin" />)}
            />
            <Route path="seekers" element={gated(Permission.SEEKER_VIEW, <AdminSeekers />)} />
            <Route path="documents" element={gated(Permission.DOCUMENT_VERIFY, <AdminDocuments />)} />
            <Route
              path="requirements"
              element={gated(Permission.RECRUITMENT_MANAGE, <AdminRequirements />)}
            />
            <Route
              path="deployments"
              element={gated(Permission.DEPLOYMENT_MANAGE, <DeploymentsPage scope="admin" />)}
            />
            <Route
              path="attendance"
              element={gated(Permission.ATTENDANCE_VIEW, <AttendancePage scope="admin" />)}
            />
            <Route
              path="invoices"
              element={gated(Permission.BILLING_VIEW, <InvoicesPage scope="admin" />)}
            />
            <Route
              path="invoices/:id"
              element={gated(Permission.BILLING_VIEW, <InvoiceDetailPage scope="admin" />)}
            />
            {/* Payroll has no employer-facing twin, and must not grow one: the
                run holds what each worker is paid, and the gap between that and
                the invoice is the margin. */}
            <Route
              path="payroll"
              element={gated(Permission.PAYROLL_VIEW, <PayrollPage />)}
            />
            <Route
              path="payroll/:id"
              element={gated(Permission.PAYROLL_VIEW, <PayrollRunDetailPage />)}
            />
            <Route path="trust" element={gated(Permission.TRUST_VIEW, <AdminTrust />)} />
            <Route path="reports" element={gated(Permission.REPORT_VIEW, <AdminReports />)} />
            <Route path="content" element={gated(Permission.CONTENT_MANAGE, <AdminContent />)} />
            <Route path="staff" element={gated(Permission.STAFF_MANAGE, <AdminStaff />)} />
            <Route path="audit" element={gated(Permission.AUDIT_VIEW, <AdminAudit />)} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
